/**
 * Os campos do formulário que mudam com o tipo do equipamento (#363, D1): o que cada tipo usa
 * e o que é obrigatório vêm de `GET /fire-equipments/types` (`descriptor.fields`), nunca de uma
 * lista escrita aqui. Dois grupos do desenho, escolhidos por `group`:
 * - `data`: agente, capacidade, fabricante, ano e descrição ("Dados do equipamento");
 * - `validity`: validade da carga e do teste hidrostático ("Validades e inspeção").
 */
import React from 'react';
import { Box, TextField } from '@mui/material';

import type {
  FireEquipmentFieldName,
  FireEquipmentLabels,
  FireEquipmentTypeDescriptor,
} from '../../../types/fireEquipment';
import type { FireFormErrors, FireFormField, FireFormValues } from '../fireEquipmentForm';
import { HELPER_TEXT_PROPS, LABEL_ABOVE } from './formFieldProps';

export type TypeFieldsGroup = 'data' | 'validity';

export interface TypeFieldsProps {
  group: TypeFieldsGroup;
  descriptor: FireEquipmentTypeDescriptor;
  labels: Pick<FireEquipmentLabels, 'agent' | 'capacityUnit'>;
  values: FireFormValues;
  errors: FireFormErrors;
  onChange: (field: FireFormField, value: string) => void;
  onBlur: (field: FireFormField) => void;
  /** Equipamento fora de uso: as validades não se mudam sem reativar. */
  scheduleLocked?: boolean;
}

const FULL_ROW = { gridColumn: { sm: '1 / -1' } } as const;

const HYDROSTATIC_HINT: Partial<Record<FireEquipmentTypeDescriptor['type'], string>> = {
  EXTINGUISHER: 'Está no cilindro ou na etiqueta de manutenção.',
  HYDRANT: 'Vale para a mangueira do hidrante.',
};

export const TypeFields: React.FC<TypeFieldsProps> = ({
  group,
  descriptor,
  labels,
  values,
  errors,
  onChange,
  onBlur,
  scheduleLocked = false,
}) => {
  const rule = (name: FireEquipmentFieldName) => descriptor.fields.find((field) => field.name === name);

  const common = (name: FireEquipmentFieldName, label: string) => ({
    label,
    required: rule(name)?.required ?? false,
    value: values[name],
    error: Boolean(errors[name]),
    InputLabelProps: LABEL_ABOVE,
    FormHelperTextProps: HELPER_TEXT_PROPS,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(name, event.target.value),
    onBlur: () => onBlur(name),
  });

  const nativeSelect = { select: true, SelectProps: { native: true } } as const;

  if (group === 'validity') {
    return (
      <>
        {rule('chargeExpiryDate') && (
          <TextField
            {...common('chargeExpiryDate', 'Validade da carga')}
            type="date"
            disabled={scheduleLocked}
            helperText={errors.chargeExpiryDate ?? 'Está na etiqueta do extintor.'}
          />
        )}
        {rule('hydrostaticExpiryDate') && (
          <TextField
            {...common('hydrostaticExpiryDate', 'Validade do teste hidrostático')}
            type="date"
            disabled={scheduleLocked}
            helperText={errors.hydrostaticExpiryDate ?? HYDROSTATIC_HINT[descriptor.type]}
          />
        )}
      </>
    );
  }

  return (
    <>
      {rule('extinguishingAgent') && (
        <TextField {...common('extinguishingAgent', 'Agente extintor')} {...nativeSelect} helperText={errors.extinguishingAgent}>
          <option value="">Selecione…</option>
          {Object.entries(labels.agent).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </TextField>
      )}
      {rule('capacityValue') && (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '2fr 1fr' }}>
          <TextField
            {...common('capacityValue', 'Capacidade')}
            inputProps={{ inputMode: 'decimal' }}
            placeholder="Ex.: 6"
            helperText={errors.capacityValue}
          />
          <TextField {...common('capacityUnit', 'Unidade')} {...nativeSelect} helperText={errors.capacityUnit}>
            {Object.entries(labels.capacityUnit).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </TextField>
        </Box>
      )}
      {rule('manufacturer') && (
        <TextField {...common('manufacturer', 'Fabricante')} placeholder="Opcional" helperText={errors.manufacturer} />
      )}
      {rule('manufactureYear') && (
        <TextField
          {...common('manufactureYear', 'Ano de fabricação')}
          inputProps={{ inputMode: 'numeric' }}
          placeholder="Ex.: 2024"
          helperText={errors.manufactureYear}
        />
      )}
      {rule('description') && (
        <TextField
          {...common('description', 'Descrição')}
          multiline
          minRows={2}
          placeholder="Ex.: Placas fotoluminescentes da rota de fuga, térreo"
          helperText={errors.description}
          sx={FULL_ROW}
        />
      )}
    </>
  );
};

export default TypeFields;
